import React from 'react'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import {
  Grid, Avatar, Card, CardContent,  Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Centralized lodash imports - much cleaner!
import { map, isUndefined } from '@/utils/lodash'

import { ARROW_COLORS } from '../constants'

const PropertyCardAnalytics = ({
  analytics,
  propertySearch,
  pricePerSqft,
  averagePerSqft,
  currencyFormat,
  percentageFormat }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const metricsBg = isDark ? 'rgba(255, 255, 255, 0.08)' : '#eff2f5'
  const labelColor = isDark
    ? 'rgba(255, 255, 255, 0.7)'
    : theme.palette.text.secondary
  const valueColor = isDark
    ? 'rgba(255, 255, 255, 0.95)'
    : theme.palette.text.primary

  const renderArrow = (arrow) => {
    if (isUndefined(arrow)) return null

    if (arrow) {
      const backgroundColor =
        arrow === 2 ? ARROW_COLORS.UP_BLUE : ARROW_COLORS.UP_GREEN
      return (
        <Avatar
          style={{
            backgroundColor,
            height: 15,
            width: 15}}
        >
          <ArrowUpwardIcon
            style={{
              color: 'white',
              fontSize: 'small'}}
          />
        </Avatar>
      )
    } else {
      return (
        <Avatar
          style={{
            backgroundColor: ARROW_COLORS.DOWN_RED,
            height: 15,
            width: 15}}
        >
          <ArrowDownwardIcon
            style={{
              color: 'white',
              fontSize: 'small'}}
          />
        </Avatar>
      )
    }
  }

  const formatValue = (field) => {
    if (field.value === '-') return field.value

    if (field.type === 'currency') {
      return currencyFormat.format(field.value)
    } else if (field.type === 'percent') {
      return field.value > 10
        ? '+1000%'
        : percentageFormat.format(field.value)
    }

    return field.value
  }

  const renderAnalyticsCard = () => (
    <Card style={{ backgroundColor: metricsBg }}>
      <CardContent style={{ padding: 10 }}>
        <Grid
          container
          direction="row"
          wrap="nowrap"
          style={{ padding: 0 }}
          sx={{
            justifyContent: "space-around",
            alignItems: "center"
          }}>
          {map(analytics, (field) => (
            <Grid
              key={field.label}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                px: 0.5,
              }}
            >
              <Grid container direction="column" sx={{
                alignItems: "center"
              }}>
                <Grid size={12} sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      lineHeight: 1.25,
                      fontSize: 13,
                      color: labelColor,
                    }}
                  >
                    {field.label}
                  </Typography>
                </Grid>
                <Grid size={12} sx={{ width: '100%' }}>
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{
                      justifyContent: "center",
                      alignItems: "center"
                    }}>
                    {renderArrow(field.arrow)}
                    <Grid sx={{ ml: '5px', minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: valueColor,
                        }}
                      >
                        {formatValue(field)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  )

  const renderPropertySearchAnalytics = () => (
    <Card
      style={{
        backgroundColor: metricsBg,
        position: 'sticky'}}
    >
      <CardContent style={{ padding: 10 }}>
        <Grid
          container
          direction="row"
          spacing={0}
          wrap="nowrap"
          style={{ padding: 0 }}
          sx={{
            justifyContent: "space-around",
            alignItems: "center"
          }}>
          <Grid key={'average'}>
            <Grid container direction="column" sx={{
              alignItems: "center"
            }}>
              <Grid size={12}>
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 13,
                    color: labelColor}}
                >
                  Average per sqft
                </Typography>
              </Grid>
              <Grid size={12}>
                <Grid
                  container
                  direction="row"
                  wrap="nowrap"
                  sx={{
                    justifyContent: "center",
                    alignItems: "center"
                  }}>
                  <Grid>
                    {pricePerSqft > averagePerSqft ? (
                      <Avatar
                        style={{
                          backgroundColor: ARROW_COLORS.UP_BLUE,
                          height: 15,
                          width: 15}}
                      >
                        <ArrowUpwardIcon
                          style={{
                            color: 'white',
                            fontSize: 'small'}}
                        />
                      </Avatar>
                    ) : pricePerSqft === averagePerSqft ? (
                      <Avatar
                        style={{
                          backgroundColor: ARROW_COLORS.UP_GREEN,
                          height: 15,
                          width: 15}}
                      >
                        <ArrowUpwardIcon
                          style={{
                            color: 'white',
                            fontSize: 'small'}}
                        />
                      </Avatar>
                    ) : (
                      <Avatar
                        style={{
                          backgroundColor: ARROW_COLORS.DOWN_RED,
                          height: 15,
                          width: 15}}
                      >
                        <ArrowDownwardIcon
                          style={{
                            color: 'white',
                            fontSize: 'small'}}
                        />
                      </Avatar>
                    )}
                  </Grid>
                  <Grid style={{ marginLeft: 5 }}>
                    <Typography
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: valueColor}}
                    >
                      {currencyFormat.format(pricePerSqft)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )

  if (propertySearch && analytics.length === 0) {
    return renderPropertySearchAnalytics()
  }

  if (analytics.length === 0) {
    return null
  }

  return renderAnalyticsCard()
}

export default PropertyCardAnalytics
